<template>
  <Vueform
    :key="initialPartialNric"
    class="w-full"
    :float-placeholders="false"
    :display-errors="false"
    :force-labels="false"
    :display-messages="false"
    v-model="formData"
    @change="handleFormData"
  >
    <div class="col-span-12">
      <TextElement
        ref="nricInput"
        name="partialNric"
        label="Last 4 chars of NRIC/FIN/Passport no."
        :placeholder="partialNricPlaceholderText"
        :default="initialPartialNric"
        :disabled="disabled"
        :rules="['required', `regex:/^[A-Za-z0-9]{4}$/`]"
        :messages="{
          required: 'Required',
          regex: 'Must contain 4 characters',
        }"
        :attrs="{
          type: 'text',
          pattern: '[A-Za-z0-9]{4}',
          maxlength: 4,
          inputmode: 'text',
        }"
        :add-classes="{
          TextElement: {
            inputContainer: 'mt-[8px] md:mt-[12px] lg:mt-[16px]',
          },
          ElementLabel: {
            container:
              'text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.125rem] font-semibold text-[#191817]',
          },
        }"
        @input="handleInput"
      />
      <div class="flex flex-row gap-1 items-center pt-[16px]">
        <img src="/assets/icons/my-giving/info.svg" class="w-[16px] h-[16px]" />
        <div
          @click="onInfo"
          class="block text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] font-inter font-medium text-[#0D5DF5] underline cursor-pointer"
        >
          What is this information used for?
        </div>
      </div>
    </div>
  </Vueform>
</template>
<script>
export default {
  data() {
    return {
      formData: {
        partialNric: null,
      },
    };
  },
  computed: {
    initialPartialNric() {
      const value = this.originalPayload?.partialNric;
      if (value !== null && value !== undefined && typeof value === 'object') {
        return null;
      }
      return value || null;
    },
  },
  props: {
    originalPayload: {
      type: Object,
      default: () => {},
    },
    partialNricPlaceholderText: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    originalPayload: {
      handler(newVal) {
        if (!newVal?.partialNric || typeof newVal.partialNric === 'object') {
          return;
        }

        const stringValue = String(newVal.partialNric);
        if (this.formData.partialNric !== stringValue) {
          this.formData.partialNric = stringValue;
          this.$nextTick(() => {
            this.$emit('handle-form-data', stringValue);
          });
        }
      },
      immediate: true,
      deep: true,
    },
    'formData.partialNric': {
      handler(newVal) {
        if (newVal === null || newVal === undefined) {
          return;
        }

        if (typeof newVal === 'object') {
          this.$nextTick(() => {
            this.formData.partialNric = null;
            this.$emit('handle-form-data', null);
          });
          return;
        }

        if (typeof newVal === 'number') {
          const stringValue = String(newVal);
          this.$nextTick(() => {
            this.formData.partialNric = stringValue;
            this.$emit('handle-form-data', stringValue);
          });
        } else if (typeof newVal === 'string') {
          const filtered = newVal.replace(/[^A-Za-z0-9]/g, '').substring(0, 4);
          if (filtered !== newVal) {
            this.$nextTick(() => {
              this.formData.partialNric = filtered;
              this.$emit('handle-form-data', filtered);
            });
          } else {
            this.$emit('handle-form-data', filtered);
          }
        }
      },
      immediate: false,
    },
  },
  mounted() {
    if (this.originalPayload?.partialNric) {
      if (typeof this.originalPayload.partialNric !== 'object') {
        const stringValue = String(this.originalPayload.partialNric);
        this.formData.partialNric = stringValue;
        this.$nextTick(() => {
          this.$emit('handle-form-data', stringValue);
        });
      }
    }

    this.$nextTick(() => {
      if (this.$refs.nricInput && this.$refs.nricInput.$el) {
        const input = this.$refs.nricInput.$el.querySelector('input');
        if (input) {
          // Store original value on focus to detect if it changes
          input.addEventListener(
            'input',
            e => {
              const inputValue = e.target.value || '';
              const filtered = inputValue.replace(/[^A-Za-z0-9]/g, '').substring(0, 4);

              this.formData.partialNric = filtered;
              this.$emit('handle-form-data', filtered);

              if (inputValue !== filtered) {
                e.target.value = filtered;
              }
            },
            { capture: true },
          );
        }
      }
    });
  },
  methods: {
    onInfo() {
      this.$emit('onInfo');
    },
    handleFormData() {
      const value = this.formData.partialNric;
      if (value === null || value === undefined) {
        this.$emit('handle-form-data', null);
        return;
      }

      if (typeof value === 'object') {
        this.formData.partialNric = null;
        this.$emit('handle-form-data', null);
        return;
      }

      if (typeof value === 'string' || typeof value === 'number') {
        const stringValue = String(value);
        const filtered = stringValue.replace(/[^A-Za-z0-9]/g, '').substring(0, 4);
        if (filtered !== stringValue) {
          this.formData.partialNric = filtered;
        }
        this.$emit('handle-form-data', filtered);
      } else {
        this.formData.partialNric = null;
        this.$emit('handle-form-data', null);
      }
    },
    handleInput(value) {
      if (value === null || value === undefined || typeof value === 'object') {
        return;
      }

      if (typeof value === 'string' || typeof value === 'number') {
        const stringValue = String(value);
        const filtered = stringValue.replace(/[^A-Za-z0-9]/g, '').substring(0, 4);

        if (this.formData.partialNric !== filtered) {
          this.formData.partialNric = filtered;
        }

        this.$emit('handle-form-data', filtered);

        this.$nextTick(() => {
          if (this.$refs.nricInput && this.$refs.nricInput.$el) {
            const input = this.$refs.nricInput.$el.querySelector('input');
            if (input && input.value !== filtered) {
              input.value = filtered;
            }
          }
        });
      }
    },
  },
};
</script>

<style scoped>
:deep(.form-input-group) {
  background-color: #ffffff !important;
  border: 1px solid #c3c2c1 !important;
  border-radius: 8px !important;
  min-height: unset !important;
}

:deep(.form-bg-disabled.form-input-group) {
  background-color: #f0f2f4 !important;
  border-color: #d3d8de !important;
  cursor: not-allowed !important;
}

:deep(input) {
  height: auto !important;
  padding: 16px !important;
  color: #21201e;
  font-size: 16px;
  line-height: 24px;
}

@media (min-width: 768px) {
  :deep(input) {
    font-size: 28px;
    line-height: 38px;
  }
}

@media (min-width: 1024px) {
  :deep(input) {
    font-size: 30px;
    line-height: 40px;
  }
}

:deep(input:disabled) {
  background-color: #f0f2f4 !important;
  color: #9ca3af !important;
  cursor: not-allowed !important;
}
</style>
